import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { Icon } from './icons';

interface GraphiteAsideProps {
  /** Open on mobile (controlled by GraphiteHeader hamburger). Desktop ignores this. */
  mobileOpen: boolean;
  /** Close handler used by backdrop click and after navigating to a new route. */
  onClose: () => void;
}

interface MenuEntry { label: string; path: string; icon: string; }
interface MenuSection { label: string; items: MenuEntry[]; }

/**
 * Nav content mirrors TerminalAside (and UserAsideMenu) — kept deliberately
 * duplicated so each theme's menu can evolve independently. Stage 5 may lift
 * this into a shared config; for now it tracks the terminal menu 1:1.
 */
const MENU_SECTIONS: MenuSection[] = [
  { label: '', items: [{ label: 'Dashboard', path: '/dashboard', icon: 'dashboard' }] },
  {
    label: 'Marketing',
    items: [
      { label: 'Analytics', path: '/analytics', icon: 'chart' },
      { label: 'Google Ads', path: '/marketing/google-ads', icon: 'megaphone' },
      { label: 'Mass email', path: '/marketing/mass-email', icon: 'mail' },
      { label: 'Website submissions', path: '/website-submissions', icon: 'globe' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { label: 'Contacts', path: '/contacts', icon: 'users' },
      { label: 'Monthly support', path: '/monthly-support', icon: 'approve' },
    ],
  },
  {
    label: 'Accounting',
    items: [
      { label: 'Invoices & bills', path: '/accounting', icon: 'receipt' },
      { label: 'Recurring payments', path: '/recurring-payments', icon: 'wallet' },
      { label: 'Cash flow', path: '/accounting/cashflow', icon: 'wallet' },
      { label: 'Bank reconciliation', path: '/banking', icon: 'bank' },
    ],
  },
  {
    label: 'Account',
    items: [{ label: 'Settings', path: '/user/account/settings', icon: 'settings' }],
  },
];

const GraphiteAside: React.FC<GraphiteAsideProps> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await signOut(getAuth());
      localStorage.clear();
      navigate('/login');
    } catch (err: any) {
      console.error('[GraphiteAside] Sign out failed:', err?.message);
    }
  };

  // A menu path "owns" the route only when the route equals it or sits beneath
  // it on a SEGMENT boundary — so /accounting doesn't swallow /accounting/cashflow.
  // When several paths match (nested routes), the longest/most-specific one wins,
  // so exactly one item ever highlights.
  const { pathname } = location;
  const matches = (path: string): boolean =>
    pathname === path || pathname.startsWith(path + '/');
  const activePath = MENU_SECTIONS
    .flatMap((s) => s.items.map((i) => i.path))
    .filter(matches)
    .sort((a, b) => b.length - a.length)[0] || '';
  const isActive = (path: string): boolean => path === activePath;

  return (
    <>
      {mobileOpen && (
        <div className="g-aside__backdrop" onClick={onClose} role="button" aria-label="Close menu" tabIndex={-1} />
      )}
      <aside className={`g-aside ${mobileOpen ? 'g-aside--mobile-open' : ''}`}>
        <div className="g-aside__brand">
          <span className="g-aside__brand-mark">WC</span>
          <span className="g-aside__brand-name">Web Construct</span>
        </div>

        <nav className="g-aside__nav">
          {MENU_SECTIONS.map((section, idx) => (
            <div key={idx} className="g-aside__section">
              {section.label && <div className="g-aside__section-label">{section.label}</div>}
              {section.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`g-aside__item ${active ? 'g-aside__item--active' : ''}`}
                    onClick={onClose}
                  >
                    <Icon name={item.icon} size={18} />
                    <span className="g-aside__label">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="g-aside__footer">
          <button type="button" className="g-aside__signout" onClick={handleSignOut}>
            <Icon name="logout" size={18} />
            <span className="g-aside__label">Sign out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default GraphiteAside;
